import { A } from "@solidjs/router";
import { createSignal, Show } from "solid-js";
import { AplombButton, AplombChip, AplombField } from "~/components";
import "./login.css";

/**
 * Login SPA — redirige vers Kratos self-service flow /self-service/login/browser.
 * Kratos gère l'affichage form + validation + WebAuthn/TOTP + rate-limit.
 * On récupère ensuite le cookie ory_kratos_session côté navigateur automatiquement.
 *
 * V2 : intégrer le flow via API (headless flow) pour UI 100% Aplomb.
 * V1 : redirect (parité fonctionnelle rapide, pas de rewrite Aplomb visuel).
 */

const KRATOS_BASE = import.meta.env.VITE_APLOMB_KRATOS_PUBLIC ?? "https://auth.aplomb.jescy.dev";

export default function Login() {
  const [email, setEmail] = createSignal("");
  const [emailError, setEmailError] = createSignal<string | undefined>();

  const startFlow = () => {
    const trimmed = email().trim();
    if (!trimmed.includes("@")) {
      setEmailError("Adresse courriel invalide");
      return;
    }
    setEmailError(undefined);
    // Kratos flow init : redirige vers /self-service/login/browser + return_to.
    const returnTo = encodeURIComponent(`${window.location.origin}/`);
    const url = `${KRATOS_BASE}/self-service/login/browser?return_to=${returnTo}&login_hint=${encodeURIComponent(trimmed)}`;
    window.location.href = url;
  };

  return (
    <>
      <header class="a-nav">
        <div class="a-container a-nav__inner">
          <A href="/" class="a-nav__brand">
            <span class="a-nav__brandmark">A|</span>
            <span class="a-nav__brandtext">Aplomb</span>
          </A>
          <A href="/pro/signup" class="a-nav__back">Devenir plombier vérifié</A>
        </div>
      </header>

      <main class="a-container a-login">
        <section>
          <AplombChip variant="neutral" compact>WebAuthn / passkey + TOTP</AplombChip>
          <h1>Se connecter à Aplomb</h1>
          <p class="a-login__intro">
            Aucun mot de passe stocké côté Aplomb. Nous utilisons Kratos (Argon2id +
            passkey WebAuthn + TOTP) et Have I Been Pwned pour bloquer les mots de passe
            compromis.
          </p>

          <div class="a-login__form">
            <AplombField
              label="Courriel"
              type="email"
              required
              autocomplete="email"
              value={email()}
              onInput={(e) => setEmail(e.currentTarget.value)}
              error={emailError()}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  startFlow();
                }
              }}
            />

            <AplombButton variant="primary" size="large" fullWidth onClick={startFlow}>
              Continuer
            </AplombButton>

            <Show when={KRATOS_BASE.includes("localhost") || KRATOS_BASE.includes("auth.aplomb")}>
              <p class="a-login__hint">
                Vous serez redirigé vers Kratos (<code>{KRATOS_BASE}</code>) pour compléter
                la vérification WebAuthn / mot de passe / OTP.
              </p>
            </Show>
          </div>

          <p class="a-login__legal">
            En vous connectant, vous acceptez les
            <A href="/cgu"> conditions d'utilisation</A> et la
            <A href="/confidentialite"> politique de confidentialité (Loi 25 QC)</A>.
          </p>

          <div class="a-login__pro-hint">
            <p>
              Vous êtes plombier ? <A href="/pro/signup">Devenez pro vérifié Aplomb.</A>
            </p>
          </div>
        </section>
      </main>
    </>
  );
}
